import React, { useMemo } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, ArrowRight } from "lucide-react";
import DOMPurify from 'dompurify';
import { THERAPPI_BRAND } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

interface ChatMessageProps {
  message: string;
  isUser: boolean;
  timestamp: Date;
  onDelete?: () => void;
  messageId?: string;
}

// Extract navigation links from message
const extractNavigationLinks = (text: string): { cleanText: string; links: { path: string; label: string }[] } => {
  const links: { path: string; label: string }[] = [];
  let cleanText = text;
  
  // Pattern 1: Markdown-style links [Label](/path)
  const markdownPattern = /\[([^\]]+)\]\((\/[\w-]+(?:\/[\w-]*)*)\)/gi;
  let match;
  while ((match = markdownPattern.exec(text)) !== null) {
    const label = match[1].trim();
    const path = match[2];
    // Skip if already added
    if (!links.some(l => l.path === path)) {
      links.push({ path, label });
    }
    cleanText = cleanText.replace(match[0], label);
  }
  
  // Pattern 2: "Access it here: /path" format
  const accessPattern = /Access it here:\s*(\/[\w-]+(?:\/[\w-]*)*)/gi;
  while ((match = accessPattern.exec(text)) !== null) {
    const path = match[1];
    const pathLabel = path.slice(1).split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    if (!links.some(l => l.path === path)) {
      links.push({ path, label: `Go to ${pathLabel}` });
    }
  }
  
  // Pattern 3: Standalone paths like "/sanctuary" or "/daily-check-in" at end of sentences
  const standalonePattern = /(?:at|visit|go to|open|try|check out|recommend)\s+(\/[\w-]+(?:\/[\w-]*)*)/gi;
  while ((match = standalonePattern.exec(text)) !== null) {
    const path = match[1];
    const pathLabel = path.slice(1).split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    if (!links.some(l => l.path === path)) {
      links.push({ path, label: `Open ${pathLabel}` });
    }
  }
  
  return { cleanText, links };
};

// Navigation button component
const NavButton: React.FC<{ path: string; label: string }> = ({ path, label }) => {
  const navigate = useNavigate();
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => navigate(path)}
      className="gap-2 text-xs font-medium animate-fade-in bg-primary/10 hover:bg-primary/20 border-primary/30"
    >
      {label}
      <ArrowRight className="h-3 w-3" />
    </Button>
  );
};

export const ChatMessage: React.FC<ChatMessageProps> = ({ 
  message, 
  isUser, 
  timestamp,
  onDelete,
  messageId
}) => {
  // Extract navigation links from AI messages
  const { cleanText, links } = useMemo(() => {
    if (isUser) return { cleanText: message, links: [] };
    return extractNavigationLinks(message);
  }, [message, isUser]);

  // Process message to convert **text** to italic styling
  const processedMessage = useMemo(() => {
    // First sanitize to prevent XSS
    const sanitized = DOMPurify.sanitize(cleanText, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
      KEEP_CONTENT: true
    });
    
    // Convert **text** to <em>text</em> for italic emphasis
    return sanitized.replace(/\*\*(.*?)\*\*/g, '<em>$1</em>');
  }, [cleanText]);

  // Sanitize the processed message but allow <em> tags
  const sanitizedMessage = useMemo(() => {
    return DOMPurify.sanitize(processedMessage, {
      ALLOWED_TAGS: ['em'],
      ALLOWED_ATTR: [],
      KEEP_CONTENT: true
    });
  }, [processedMessage]);

  return (
    <div className={`flex mb-6 ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] ${isUser ? 'order-1' : 'order-2'}`}>
        <Card className={`p-4 shadow-gentle transition-gentle ${
          isUser 
            ? 'bg-primary text-primary-foreground ml-4' 
            : 'bg-muted/50 text-foreground mr-4 border-2'
        }`}>
          <div className="flex justify-between items-start gap-2">
            <p 
              className={`text-sm leading-relaxed whitespace-pre-wrap flex-1 ${
                isUser ? 'font-medium' : '[&_em]:text-foreground [&_em]:font-normal [&_em]:opacity-100 [&_em]:brightness-150'
              }`}
              dangerouslySetInnerHTML={{ __html: sanitizedMessage }}
            />
            <div className="flex gap-1 flex-shrink-0">
              {onDelete && messageId && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDelete}
                  className="opacity-70 hover:opacity-100 hover:text-destructive p-1 h-auto"
                  title="Delete this message"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
          
          {/* Navigation buttons for AI messages */}
          {!isUser && links.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-border/50">
              {links.map((link, index) => (
                <NavButton key={`${link.path}-${index}`} path={link.path} label={link.label} />
              ))}
            </div>
          )}
          
          <div className="text-xs opacity-70 mt-2">
            {isUser ? 'You' : THERAPPI_BRAND} • {timestamp.toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </div>
        </Card>
      </div>
    </div>
  );
};